import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export default function OAuthTestPage() {
  const [logs, setLogs] = useState<string[]>([])
  const [session, setSession] = useState<any>(null)

  const addLog = (message: string) => {
    console.log(message)
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`])
  }

  useEffect(() => {
    if (!supabase) {
      addLog('❌ Supabase client is NULL')
      return
    }

    addLog('✅ Supabase client initialized')

    // Check current session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        addLog(`❌ Session error: ${error.message}`)
      } else if (session) {
        addLog(`✅ Active session found: ${session.user.email}`)
        setSession(session)
      } else {
        addLog('ℹ️ No active session')
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      addLog(`🔔 Auth event: ${event}`)
      if (session) {
        addLog(`👤 User: ${session.user.email}`)
        setSession(session)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleGoogleSignIn = async () => {
    if (!supabase) {
      addLog('❌ Cannot sign in - Supabase not initialized')
      return
    }

    addLog('🚀 Starting Google OAuth...')
    addLog(`📍 Redirect URL: ${window.location.origin}/oauth-test`)

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/oauth-test`,
        },
      })

      if (error) {
        addLog(`❌ OAuth error: ${error.message}`)
      } else {
        addLog(`✅ OAuth initiated: ${JSON.stringify(data)}`)
      }
    } catch (err: any) {
      addLog(`❌ Exception: ${err.message}`)
    }
  }

  const handleSignOut = async () => {
    if (!supabase) return
    
    addLog('🚪 Signing out...')
    const { error } = await supabase.auth.signOut()
    
    if (error) {
      addLog(`❌ Sign out error: ${error.message}`)
    } else {
      addLog('✅ Signed out successfully')
      setSession(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">OAuth Debug Page</h1>
        
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Current Status</h2>
          <div className="space-y-2">
            <p>Supabase URL: {import.meta.env.VITE_SUPABASE_URL || '❌ Not set'}</p>
            <p>Supabase Key: {import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Set' : '❌ Not set'}</p>
            <p>Client: {supabase ? '✅ Initialized' : '❌ NULL'}</p>
            <p>Session: {session ? `✅ ${session.user.email}` : '❌ None'}</p>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Actions</h2>
          <div className="flex gap-4">
            <button
              onClick={handleGoogleSignIn}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium"
            >
              Sign In with Google
            </button>
            {session && (
              <button
                onClick={handleSignOut}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-medium"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Event Log</h2>
          <div className="bg-gray-900 rounded p-4 font-mono text-sm space-y-1 max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-500">No events yet...</p>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="text-green-400">{log}</div>
              ))
            )}
          </div>
        </div>

        {session && (
          <div className="bg-gray-800 rounded-lg p-6 mt-6">
            <h2 className="text-xl font-semibold mb-4">Session Data</h2>
            <pre className="bg-gray-900 rounded p-4 text-xs overflow-x-auto">
              {JSON.stringify(session, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
